/**
 * PATH: packages/email-hub/src/feature/components/filter-sidebar/SidebarChrome.tsx
 * FILE_ID: EMAILHUB-FILTERSIDEBAR-CHROME-001
 * PURPOSE: Shared header controls for the email filter sidebar and its sections.
 */
import { useState } from 'react'
import {
  BookmarkPlus,
  ChevronDown,
  ChevronRight,
  Filter,
  X,
} from 'lucide-react'

export function SidebarHeader({
  activeCount,
  hasActive,
  onClear,
  onSave,
}: {
  activeCount: number
  hasActive: boolean
  onClear: () => void
  onSave: (name: string) => void | Promise<void>
}) {
  const [savingName, setSavingName] = useState<string>('')
  const [saveOpen, setSaveOpen] = useState(false)

  return (
    <div className="flex items-center justify-between mb-3 gap-2">
      <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
        <Filter className="h-3.5 w-3.5" />
        Filters
        {activeCount > 0 && (
          <span className="text-[10px] bg-primary/15 text-primary px-1.5 py-px rounded-full font-medium">
            {activeCount}
          </span>
        )}
      </h3>
      <div className="flex items-center gap-1.5">
        {hasActive && (
          <button
            onClick={() => setSaveOpen((v) => !v)}
            className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
            title="Save current filters as a named view"
          >
            <BookmarkPlus className="h-2.5 w-2.5" /> Save
          </button>
        )}
        {hasActive && (
          <button
            onClick={onClear}
            className="text-[10px] text-muted-foreground hover:underline flex items-center gap-0.5"
          >
            <X className="h-2.5 w-2.5" /> Clear
          </button>
        )}
      </div>
      {saveOpen && (
        <div className="absolute z-10 mt-8 left-3 right-3 bg-popover border border-border rounded-md shadow-lg p-2">
          <input
            type="text"
            placeholder="Name this view…"
            value={savingName}
            onChange={(e) => setSavingName(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && savingName.trim()) {
                await onSave(savingName.trim())
                setSavingName('')
                setSaveOpen(false)
              }
              if (e.key === 'Escape') {
                setSavingName('')
                setSaveOpen(false)
              }
            }}
            className="w-full text-xs px-2 py-1 border border-border rounded bg-background text-foreground"
            autoFocus
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Press Enter to save · Esc to cancel
          </p>
        </div>
      )}
    </div>
  )
}

export function SectionHeader({
  label,
  expanded,
  onToggle,
  count,
}: {
  label: string
  expanded: boolean
  onToggle: () => void
  count?: number
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between text-xs font-medium text-muted-foreground mb-1.5"
    >
      <span className="flex items-center gap-1.5">
        {label}
        {count !== undefined && count > 0 && (
          <span className="text-[9px] bg-muted text-muted-foreground px-1 py-px rounded">
            {count}
          </span>
        )}
      </span>
      {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
    </button>
  )
}
